import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Injects a JSON-LD <script type="application/ld+json"> into <head>.
 * Structured data helps search engines (Google, Bing) and LLMs
 * (ChatGPT, Perplexity, Claude) understand the page's entities.
 */
const JsonLd = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

// ---------- Shared schema builders ----------

const SITE_URL = 'https://imprint-mis.co.uk';
const LOGO_URL = `${SITE_URL}/imprint-logo-color.svg`;

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Imprint MIS',
  legalName: 'Imprint Business Systems Limited',
  url: SITE_URL,
  logo: LOGO_URL,
  description:
    'UK-based Management Information Systems specialists for the print and packaging industry. MIS integration, workflow automation, JDF and machine data solutions.',
  sameAs: [
    'https://www.linkedin.com/company/imprint-management-information-systems-limited/',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+44-1245-231-670',
      contactType: 'sales',
      email: 'info@imprint-mis.co.uk',
      areaServed: 'GB',
      availableLanguage: 'en',
    },
  ],
};

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#business`,
  name: 'Imprint MIS',
  image: LOGO_URL,
  url: SITE_URL,
  telephone: '+44-1245-231-670',
  email: 'info@imprint-mis.co.uk',
  priceRange: '£££',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '27 Fairycroft Road',
    addressLocality: 'Saffron Walden',
    addressRegion: 'Essex',
    postalCode: 'CB10 1LZ',
    addressCountry: 'GB',
  },
  areaServed: [
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'Country', name: 'Ireland' },
    { '@type': 'Country', name: 'Germany' },
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Imprint MIS',
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#business` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/#resources?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const articleSchema = (post, imageUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: post.title,
  description: post.excerpt || post.title,
  image: imageUrl ? [imageUrl] : [LOGO_URL],
  datePublished: post.created_at,
  dateModified: post.updated_at || post.created_at,
  author: { '@type': 'Organization', name: 'Imprint MIS', url: SITE_URL },
  publisher: {
    '@type': 'Organization',
    name: 'Imprint MIS',
    logo: { '@type': 'ImageObject', url: LOGO_URL },
  },
  mainEntityOfPage: `${SITE_URL}/resources/${post.slug}`,
  articleSection: post.category,
});

export const teamPersonSchemas = (team) =>
  team.map((person) => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: person.name,
    jobTitle: person.role,
    image: `${SITE_URL}${person.image}`,
    worksFor: { '@id': `${SITE_URL}/#business` },
    ...(person.linkedin ? { sameAs: [person.linkedin] } : {}),
  }));

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
});

export const itemListSchema = (name, urls) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  itemListElement: urls.map((url, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${SITE_URL}${url}`,
  })),
});

export default JsonLd;
